import { cn, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { CaretRightIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { DocumentTable } from "../components/document-table/document-table";
import type { DocumentData } from "../components/document-table/types";
import { useDocumentActions } from "../components/document-table/use-document-actions";
import { DocumentsPanel, interventionLots } from "../components/study-progress";

type DateSummary = { date: string | null; interventionCount: number };
type FilterOption = { id: number; name: string };
type DatesData = {
  filters: {
    lots: FilterOption[];
    performances: Array<FilterOption & { lotId: number }>;
    buildings: Array<{ id: number; name: string; shortName: string | null }>;
  };
  dates: DateSummary[];
};
type DateIntervention = {
  id: number;
  dateIntervention: string | null;
  building: { id: number; name: string; shortName: string | null };
  zone: { id: number; name: string; shortName: string | null };
  lot: { id: number; name: string; nLot: string | null };
  performance: { id: number; name: string };
  rankOneContracts: Array<{ id: number; label: string; companyName: string }>;
  forecastDocumentCount: number;
  producedDocumentCount: number;
  latestFavorableDocumentCount: number;
  documents: DocumentData[];
};
type PageState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; data: DatesData };
type DetailState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; interventions: DateIntervention[] };

const parseIds = (value: string | null) =>
  value
    ?.split(",")
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0) ?? [];

const formatDate = (date: string | null) =>
  isNil(date)
    ? "Sans date"
    : new Date(`${date}T00:00:00`).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });

export function InterventionsAVenirDo() {
  const { yardId } = useParams<{ yardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ status: "loading" });
  const [selectedDate, setSelectedDate] = useState<string | null | undefined>(
    undefined,
  );
  const [detail, setDetail] = useState<DetailState>({ status: "idle" });
  const [openInterventionId, setOpenInterventionId] = useState<number | null>(
    null,
  );
  const lotQuery = searchParams.get("lot") ?? "";
  const performanceQuery = searchParams.get("performance") ?? "";
  const buildingQuery = searchParams.get("building") ?? "";
  const recentOnly = searchParams.get("recentOnly") === "true";

  useEffect(() => {
    if (isNil(yardId)) return;
    let cancelled = false;
    // Keep the screen in place while it reloads: unmounting the filters would
    // close the list being ticked.
    setState((current) =>
      current.status === "success" ? current : { status: "loading" },
    );
    void apiClient
      .GET("/api/yards/{yardId}/study-progress/oversight-dates", {
        params: {
          path: { yardId },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
            ...(buildingQuery ? { building: buildingQuery } : {}),
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setState({ status: "error" });
          return;
        }
        setState({ status: "success", data: data.data });
        setSelectedDate((current) =>
          // biome-ignore lint/plugin/no-nil-comparison: undefined = no date selected, null = the undated bucket
          current !== undefined &&
          data.data.dates.some((item) => item.date === current)
            ? current
            : (data.data.dates[0]?.date ?? undefined),
        );
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [yardId, lotQuery, performanceQuery, buildingQuery]);

  useEffect(() => {
    // biome-ignore lint/plugin/no-nil-comparison: undefined = no date selected, null = the undated bucket
    if (isNil(yardId) || selectedDate === undefined) {
      setDetail({ status: "idle" });
      return;
    }
    let cancelled = false;
    setDetail({ status: "loading" });
    void apiClient
      .GET("/api/yards/{yardId}/study-progress/oversight-dates/{date}", {
        params: {
          path: { yardId, date: selectedDate ?? "none" },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
            ...(buildingQuery ? { building: buildingQuery } : {}),
            recentOnly: recentOnly ? "true" : "false",
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setDetail({ status: "error" });
          return;
        }
        setDetail({
          status: "success",
          interventions: data.data.interventions,
        });
      })
      .catch(() => {
        if (!cancelled) setDetail({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [
    yardId,
    selectedDate,
    lotQuery,
    performanceQuery,
    buildingQuery,
    recentOnly,
  ]);

  const updateFilter = (
    key: "lot" | "performance" | "building",
    values: string[],
  ) => {
    const next = new URLSearchParams(searchParams);
    if (values.length === 0) next.delete(key);
    else next.set(key, values.join(","));
    setSearchParams(next, { replace: true });
  };
  const setRecentOnly = (checked: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (checked) next.set("recentOnly", "true");
    else next.delete("recentOnly");
    setSearchParams(next, { replace: true });
  };

  // A donneur d'ordre changes nothing from this table.
  const { tableProps, overlays } = useDocumentActions({
    yardId,
    lots:
      detail.status === "success"
        ? detail.interventions.flatMap(interventionLots)
        : [],
    onChanged: () => undefined,
  });
  const openIntervention =
    detail.status === "success"
      ? detail.interventions.find((item) => item.id === openInterventionId)
      : undefined;

  if (state.status === "loading")
    return <PageMessage text="Chargement des interventions…" />;
  if (state.status === "error")
    return (
      <PageMessage text="Impossible de charger les interventions." error />
    );

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden p-4">
      <header className="shrink-0">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
          Interventions à venir
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          Visualisez les interventions de vos bâtiments par date de démarrage.
        </p>
      </header>
      <section
        aria-label="Filtres"
        className="flex shrink-0 flex-wrap items-end gap-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
      >
        <div className="w-56">
          <Select
            label="Lots"
            multiple
            options={state.data.filters.lots.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={parseIds(lotQuery).map(String)}
            onValueChange={({ value }) => updateFilter("lot", value)}
          />
        </div>
        <div className="w-56">
          <Select
            label="Prestations"
            multiple
            options={state.data.filters.performances.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={parseIds(performanceQuery).map(String)}
            onValueChange={({ value }) => updateFilter("performance", value)}
          />
        </div>
        <div className="w-56">
          <Select
            label="Bâtiments"
            multiple
            options={state.data.filters.buildings.map((item) => ({
              value: String(item.id),
              label: item.shortName ?? item.name,
            }))}
            value={parseIds(buildingQuery).map(String)}
            onValueChange={({ value }) => updateFilter("building", value)}
          />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-zinc-700 dark:text-zinc-300">
          <input
            type="checkbox"
            checked={recentOnly}
            onChange={(event) => setRecentOnly(event.target.checked)}
          />
          Indices récents uniquement
        </label>
      </section>
      {state.data.dates.length === 0 ? (
        <PageMessage text="Aucune intervention ne correspond aux filtres." />
      ) : (
        <div className="relative grid min-h-0 flex-1 gap-5 overflow-y-auto lg:grid-cols-[240px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden">
          <nav
            aria-label="Dates des interventions"
            className="space-y-1 lg:overflow-y-auto"
          >
            {state.data.dates.map((item) => (
              <button
                key={item.date ?? "none"}
                type="button"
                onClick={() => setSelectedDate(item.date)}
                className={cn(
                  "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm",
                  item.date === selectedDate
                    ? "bg-blue-600 text-white"
                    : "bg-zinc-100 text-zinc-800 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-100",
                )}
              >
                <span>{formatDate(item.date)}</span>
                <span className="text-xs opacity-75">
                  {item.interventionCount}
                </span>
              </button>
            ))}
          </nav>
          <main className="min-w-0 space-y-3 lg:overflow-y-auto">
            <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">
              {/* biome-ignore lint/plugin/no-nil-comparison: undefined = no date selected, null = the undated bucket */}
              {selectedDate === undefined ? "" : formatDate(selectedDate)}
            </h3>
            <DateDetail
              state={detail}
              onOpenDocuments={setOpenInterventionId}
            />
          </main>
          {isNotNil(openIntervention) && (
            <DocumentsPanel
              title={`Détail des documents : ${interventionLabel(openIntervention)}`}
              onClose={() => setOpenInterventionId(null)}
            >
              {openIntervention.documents.length === 0 ? (
                <p className="rounded-lg bg-zinc-100 px-4 py-6 text-center text-sm text-zinc-500 dark:bg-zinc-800/50">
                  Aucun document accessible pour cette intervention.
                </p>
              ) : (
                <DocumentTable
                  lots={interventionLots(openIntervention)}
                  {...tableProps}
                />
              )}
            </DocumentsPanel>
          )}
        </div>
      )}
      {overlays}
    </div>
  );
}

function PageMessage({
  text,
  error = false,
}: {
  text: string;
  error?: boolean;
}) {
  return (
    <div
      className={cn(
        "p-8 text-center text-sm",
        error ? "text-red-600" : "text-zinc-500",
      )}
    >
      {text}
    </div>
  );
}

function interventionLabel(item: DateIntervention) {
  const building = item.building.shortName ?? item.building.name;
  const lot = item.lot.nLot ? `Lot ${item.lot.nLot} — ` : "";
  return `${building} · ${item.zone.name} · ${lot}${item.performance.name}`;
}

type DateDetailProps = {
  state: DetailState;
  onOpenDocuments: (interventionId: number) => void;
};
function DateDetail({ state, onOpenDocuments }: DateDetailProps) {
  if (state.status === "idle" || state.status === "loading")
    return <PageMessage text="Chargement des interventions…" />;
  if (state.status === "error")
    return (
      <PageMessage
        text="Impossible de charger les interventions de cette date."
        error
      />
    );
  if (state.interventions.length === 0)
    return (
      <PageMessage text="Aucune intervention ne correspond aux filtres." />
    );
  return (
    <div className="space-y-4">
      {state.interventions.map((item) => (
        <InterventionCard
          key={item.id}
          intervention={item}
          onOpenDocuments={() => onOpenDocuments(item.id)}
        />
      ))}
    </div>
  );
}

function InterventionCard({
  intervention,
  onOpenDocuments,
}: {
  intervention: DateIntervention;
  onOpenDocuments: () => void;
}) {
  return (
    <article className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <button
        type="button"
        onClick={onOpenDocuments}
        className="flex w-full items-start justify-between gap-3 text-left"
      >
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-500">
            {intervention.building.shortName ?? intervention.building.name} ·{" "}
            {intervention.zone.name}
          </p>
          <h4 className="mt-1 font-semibold text-zinc-900 dark:text-zinc-100">
            {intervention.lot.nLot ? `Lot ${intervention.lot.nLot} — ` : ""}
            {intervention.performance.name}
          </h4>
          <p className="mt-1 text-sm text-zinc-500">
            {intervention.rankOneContracts.length === 0
              ? "Aucun contrat travaux rang 1 actif"
              : intervention.rankOneContracts
                  .map(
                    (contract) => `${contract.label} (${contract.companyName})`,
                  )
                  .join(" · ")}
          </p>
          <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
            {intervention.forecastDocumentCount} document(s) prévisionnel(s) ·{" "}
            {intervention.producedDocumentCount} document(s) produit(s) ·{" "}
            {intervention.latestFavorableDocumentCount} dernière(s) version(s)
            visée(s) intégralement favorablement
          </p>
        </div>
        <CaretRightIcon />
      </button>
    </article>
  );
}
